import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { StatusMessage } from '@hvcgroep/design-system';

import groupBy from '../../../../../shared/helpers/groupBy';

import Calendar from '../../../components/Calendar';
import CalendarLegend from '../../../components/Calendar/Legend';
import { CalendarItem } from '../../../types';

interface Props {
  items: CalendarItem[];
  date: Date;
}

const ViewCalendarYear: React.FC<Props> = ({ items, date }) => {
  const { t } = useTranslation();
  const selectedYear = date.getFullYear();
  const currentDate = new Date();
  const groupedByYear = groupBy(items, (item) => String(new Date(item.date).getFullYear()));

  if (!groupedByYear[selectedYear]) {
    return (
      <StatusMessage type="info">
        {t('waste_calendar.states.empty.pickup_dates_in_year', { year: date.getFullYear() })}
      </StatusMessage>
    );
  }

  return (
    <>
      {Object.entries(groupedByYear).map(([year, yearItems]) => {
        // Only render items of the selected year.
        if (String(selectedYear) !== year) {
          return null;
        }

        const keyYear = `year:${year}`;
        const classNamesYear = c('CalendarWidget__ViewYear', `CalendarWidget__ViewYear--${year}`);
        const groupedByMonth = groupBy(yearItems, (item) => String(new Date(item.date).toLocaleString('default', { month: 'numeric' })));

        return (
          <ol key={keyYear} className={classNamesYear}>
            {Object.entries(groupedByMonth).map(([month, monthItems]) => {
              const newDate = new Date();
              // Make sure to set the date to 1 BEFORE setting the month.
              newDate.setDate(1);
              newDate.setMonth(Number(month) - 1);
              newDate.setFullYear(Number(year));

              // Only render items from the previous month and beyond.
              // That is, if we're rendering items of the current year.
              // If we're rendering items of next year we can just render it all.
              // note that currentDate.getMonth() is zero-indexed and actually means "last month".
              if (selectedYear === currentDate.getFullYear() && Number(month) < (currentDate.getMonth())) {
                return null;
              }

              const keyMonth = `year:${year}-month:${month}`;
              const classNamesMonth = c('CalendarWidget__ViewYear__month', `CalendarWidget__ViewYear__month--${year}-${month}`);
              const title = newDate.toLocaleString('nl-NL', { month: 'long', year: 'numeric' });

              return (
                <li key={keyMonth} className={classNamesMonth}>
                  <h3 className="h5 CalendarWidget__ViewYear__month__title">{title}</h3>
                  <Calendar items={monthItems} date={newDate} />
                </li>
              );
            })}
          </ol>
        );
      })}

      <CalendarLegend />
    </>
  );
};

export default ViewCalendarYear;
